import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  isReassociateHarEntriesConflictError,
  type ReassociateHarEntriesTarget,
} from '../../clients/tester-client/portal/reassociate-har-entries.schemas';
import { useLayoutStore } from '../../store/layoutStore';
import { leaveDeletedSourceEndpointView } from '../../utils/reassociate-har-entries/leave-deleted-source-endpoint-view';
import { TRACKED_HAR_ENTRIES_QUERY_KEY } from './useTrackedHarEntries';

const APPLICATION_DOMAIN_TREE_QUERY_KEY = 'application-domain-tree';

type ReassociateHarEntriesVariables = {
  applicationId: string;
  sourceEndpointId: string;
  harEntryIds: string[];
  target: ReassociateHarEntriesTarget;
};

export function useReassociateHarEntries() {
  const queryClient = useQueryClient();
  const t = useTranslations('proxy.requests.reassociate');
  const selectedRequestsContentId = useLayoutStore(
    (state) => state.selectedRequestsContentId,
  );
  const setSelectedRequestsContentId = useLayoutStore(
    (state) => state.setSelectedRequestsContentId,
  );

  return useMutation({
    mutationFn: ({
      sourceEndpointId,
      harEntryIds,
      target,
    }: ReassociateHarEntriesVariables) =>
      terraProxyApiClient.reassociateHarEntries({
        id: sourceEndpointId,
        harEntryIds,
        target,
      }),
    onSuccess: (result, { applicationId }) => {
      const layout = useLayoutStore.getState();

      leaveDeletedSourceEndpointView({
        sourceEndpointId: result.sourceEndpointId,
        tabs: layout.mainContentTabs,
        activeTabId: layout.activeMainContentTabId,
        tabNavigationByTabId: layout.tabNavigationByTabId,
        goBackInActiveTab: layout.goBackInActiveMainContentTab,
        navigateInActiveTab: layout.navigateInActiveMainContentTab,
        closeTab: layout.closeMainContentTab,
      });

      if (!result.sourceDeleted) {
        void queryClient.invalidateQueries({
          queryKey: [
            TRACKED_HAR_ENTRIES_QUERY_KEY,
            applicationId,
            result.sourceEndpointId,
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: ['endpoint-details-v2', result.sourceEndpointId],
        });
      }

      const targetEndpointIds = result.targetEndpointIds ?? [
        result.targetEndpointId,
      ];

      for (const targetEndpointId of targetEndpointIds) {
        void queryClient.invalidateQueries({
          queryKey: [
            TRACKED_HAR_ENTRIES_QUERY_KEY,
            applicationId,
            targetEndpointId,
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: ['endpoint-details-v2', targetEndpointId],
        });
      }

      void queryClient.invalidateQueries({
        queryKey: ['endpoint-signals-paginated'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['endpoints-preview'],
      });
      void queryClient.invalidateQueries({
        queryKey: [APPLICATION_DOMAIN_TREE_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: ['assets-unified', applicationId],
        exact: false,
      });
      void queryClient.invalidateQueries({
        queryKey: ['asset-counts', applicationId],
        exact: false,
      });

      if (
        selectedRequestsContentId &&
        result.movedHarEntryIds.includes(selectedRequestsContentId)
      ) {
        setSelectedRequestsContentId(null);
      }

      toasterApi.success({ title: t('success') });
    },
    onError: (error: Error) => {
      if (
        isReassociateHarEntriesConflictError(error) &&
        error.collision === 'active'
      ) {
        return;
      }

      toasterApi.error({
        title:
          isReassociateHarEntriesConflictError(error) &&
          error.collision === 'archived'
            ? t('archivedTitle')
            : t('error'),
        description: error.message,
      });
    },
  });
}
